import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "context-menu-bar-exports" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Open" }, null, -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Plus" }, null, -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Delete" }, null, -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Dictionary" }, null, -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", null, [
  /*#__PURE__*/_createElementVNode("i", { class: "mdp-History" })
], -1)

import { Guid } from "@/util/guid";
import { useToast } from "@common/composable/toast";
import { ExportProjectModel } from "@medapro-types/Exports/Exports";
import { ExportsActions, ExportsGetter, ExportsState } from "@medapro-types/Exports/store/exports";
import exportApi from "@services/exports";
import { AxiosError } from "axios";
import emitter from "tiny-emitter/instance";
import { computed, onMounted, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import menubar from "../../common/components/MenuBar/MenuBar.vue";
import { ExportType } from "@medapro-types/Exports/export-enum";
import TranslationTableDialog from "./FastPage/TranslationTableDialog.vue";
import MultiLevelDropDownButton from "@common/components/MultiLevelDropDownButton.vue";
import { NavMenuItem } from "@medapro-types/common/common";

//#region General
type ViewModel = {
  selectedExportProject: ExportProjectModel | null;
  hasPreviousExport: boolean;
  showTranslationDialog: boolean;
  navMenuItems: NavMenuItem[];
};

export default /*#__PURE__*/_defineComponent({
  __name: 'ContextMenuBar',
  setup(__props) {

const { t: $t, te: $te } = useI18n();
const route = useRoute();
const router = useRouter();
const { createToast } = useToast();
//#endregion

const { useState: useExportState, useActions } = createNamespacedHelpers<ExportsState, ExportsGetter, ExportsActions>("exports");
const { exportProjects } = useExportState(["exportProjects", "loading"]);
const { getAllExports, removeExport } = useActions(["getAllExports", "removeExport"]);
//#endregion
const { showManageExport } = useExportState(["showManageExport"]);
const { setShowManageExport } = useActions(["setShowManageExport"]);
const navMenuItems = computed<NavMenuItem[]>(() => [
  { text: $t("webReportDesigner"), icon: "mdp-ListAndLabel", value: "web-report-designer" },
  { text: $t("desktopDesigner"), icon: "no-icon", value: "desktop-designer" }
]);

const handleMenuItem = (item: NavMenuItem) => {
  if (item == null || item?.value === "web-report-designer") {
    checkIfSavedExport();
  } else if (item?.value === "desktop-designer") {
    openWindowsDesigner();
  }
};
//#region ViewModel
const vm = reactive<ViewModel>({
  selectedExportProject: null,
  hasPreviousExport: false,
  showTranslationDialog: false,
  navMenuItems
});
//#endregion

const openManageExport = () => {
  setShowManageExport(true);
};

const isNew = computed(() => {
  return route.params.id == Guid.empty;
});

const refreshSelection = () => {
  if (!route.params.id) {
    return;
  }
  const project = exportProjects.value?.find((x) => x.exportId === route.params.id);
  if (project) {
    handleExportProjectSelection(project);
  } else if (route.name == "create-export" && route.params.id !== Guid.empty && exportProjects.value.length > 0) {
    createToast({
      text: $t("api.errors.InvalidExport"),
      title: $t("Error"),
      variant: "danger",
      titleIcon: "Export",
      textIllustration: "History",
      statusTextIcon: "XCircleSolid"
    });
    router.replace({ name: "create-export", params: { type: route.params.type, id: Guid.empty } });
  }
};

const exportType = computed<ExportType>(() => {
  return route.params.type as ExportType;
});

onMounted(async () => {
  await getAllExports({ type: exportType.value });
  vm.hasPreviousExport = await hasAnyLastExportRun(route.params.id);
});

watch(
  () => exportProjects,
  () => {
    refreshSelection();
  },
  { deep: true }
);

watch(
  () => route.params.id,
  async (nv) => {
    if (route.name == "create-export") {
      refreshSelection();
    }
    if (nv) {
      vm.hasPreviousExport = await hasAnyLastExportRun(nv);
    }
  }
);

//#region Handle Export Project Selection
const handleExportProjectSelection = (exportProject: ExportProjectModel) => {
  const options = exportProject?.options;
  const optionsObject = JSON.parse(options);
  if (exportProject.exportId !== route.params.id) {
    router.push({
      name: "create-export",
      params: { type: optionsObject?.ExportSettings?.ExportCriteria?.TargetType ?? route.params.type, id: exportProject.exportId }
    });
  } else {
    vm.selectedExportProject = exportProject;
  }
};
//#endregion

//#region Reset Export Data

function resetData() {
  emitter.emit("reset-export-data");
}

//#endregion
function checkIfSavedExport(e?: any) {
  if (route.params.id == Guid.empty) {
    if (confirm($t("exports.createExport.fastpage.designerRedirectionConfirmation"))) {
      e.preventDefault();
    }
  } else {
    router.push({ name: "fastpage-designer", params: { exportId: route.params.id } });
  }
}

function openWindowsDesigner(e?: any) {
  if (route.params.id == Guid.empty) {
    if (confirm($t("exports.createExport.fastpage.designerRedirectionConfirmation"))) {
      e.preventDefault();
    }
  } else {
    router.push({ name: "fastpage-desktop-designer", params: { exportId: route.params.id } });
  }
}

//#region Delete Export
const isDeleteDisabled = computed(() => {
  return vm.selectedExportProject == null || vm.selectedExportProject.exportId == null || vm.selectedExportProject.exportId == Guid.empty;
});

async function deleteExport() {
  if (vm.selectedExportProject == null) {
    return;
  }
  if (!confirm($t("exports.createExport.deleteExportConfirmation") as string)) {
    return;
  }
  await removeExport(vm.selectedExportProject?.exportId);
  emitter.emit("reset-export-after-delete");
  vm.selectedExportProject = null;
}

async function hasAnyLastExportRun(id: string) {
  return await exportApi.isLastExportEnabled(id);
}

async function goToLastExport() {
  try {
    const exportRunId = await exportApi.getLastExport(vm.selectedExportProject?.exportId);
    if (exportRunId !== Guid.empty) router.push({ name: "progress-export", params: { type: exportType.value, id: exportRunId } });
    else
      createToast({
        text: $t("api.errors.InvalidExportRun"),
        title: $t("Error"),
        variant: "danger",
        titleIcon: "Export",
        textIllustration: "History",
        statusTextIcon: "XCircleSolid"
      });
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      let errorMessage = "";
      if (error.response?.status === 409) {
        errorMessage = $t("api.errors.InvalidExport");
      } else {
        errorMessage = $te(`api.errors.${error?.response?.data.error}`) ? $t(`api.errors.${error?.response?.data.error}`) : error?.response?.data.error;
      }
      createToast({
        text: errorMessage,
        title: $t("Error"),
        variant: "danger",
        titleIcon: "Export",
        textIllustration: "History",
        statusTextIcon: "XCircleSolid"
      });
    }
  }
}

async function showTranslationModal() {
  vm.showTranslationDialog = true;
}

//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_dropdown_item = _resolveComponent("b-dropdown-item")!
  const _component_b_dropdown = _resolveComponent("b-dropdown")!
  const _component_b_button = _resolveComponent("b-button")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(menubar, null, {
      leftItems: _withCtx(() => [
        _createVNode(_component_b_dropdown, {
          "toggle-class": "mono",
          class: "dropdown-default",
          "data-cy": "export-open-ddn"
        }, {
          "button-content": _withCtx(() => [
            _createElementVNode("span", null, _toDisplayString(vm.selectedExportProject?.name ?? _unref(exportProjects)[0]?.name), 1)
          ]),
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(exportProjects), (exportProject) => {
              return (_openBlock(), _createBlock(_component_b_dropdown_item, {
                active: (vm.selectedExportProject ? vm.selectedExportProject?.exportId : _unref(exportProjects)[0]?.exportId) === exportProject.exportId,
                onClick: ($event: any) => (handleExportProjectSelection(exportProject)),
                key: exportProject.exportId
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(exportProject.name), 1)
                ]),
                _: 2
              }, 1032, ["active", "onClick"]))
            }), 128))
          ]),
          _: 1
        }),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(_component_b_button, {
            class: "btn-icon mono",
            onClick: openManageExport,
            disabled: _unref(showManageExport)
          }, {
            default: _withCtx(() => [
              _hoisted_2
            ]),
            _: 1
          }, 8, ["disabled"])
        ])), [
          [_directive_tooltip, _unref($t)('ManageProjects')]
        ]),
        _withDirectives((_openBlock(), _createBlock(_component_b_button, {
          class: "btn-icon mono",
          onClick: resetData,
          disabled: isNew.value || _unref(showManageExport),
          "data-cy": "export-add-new-btn"
        }, {
          default: _withCtx(() => [
            _hoisted_3
          ]),
          _: 1
        }, 8, ["disabled"])), [
          [_directive_tooltip, _unref($t)('new')]
        ]),
        _withDirectives((_openBlock(), _createBlock(_component_b_button, {
          class: "btn-icon mono",
          onClick: deleteExport,
          disabled: isNew.value || isDeleteDisabled.value || _unref(showManageExport),
          "data-cy": "export-delete-btn"
        }, {
          default: _withCtx(() => [
            _hoisted_4
          ]),
          _: 1
        }, 8, ["disabled"])), [
          [_directive_tooltip, _unref($t)('delete')]
        ])
      ]),
      rightItems: _withCtx(() => [
        (exportType.value == 'FastPage')
          ? (_openBlock(), _createBlock(_component_b_button, {
              key: 0,
              class: "btn-with-icon mono",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (showTranslationModal()))
            }, {
              default: _withCtx(() => [
                _hoisted_5,
                _createElementVNode("span", null, _toDisplayString(_unref($t)("translation.table")), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (exportType.value == 'FastPage')
          ? (_openBlock(), _createBlock(MultiLevelDropDownButton, {
              key: 1,
              split: true,
              level: 1,
              "data-cy": "fastpage-layout-designer-ddn",
              "button-label": _unref($t)('layoutDesigner'),
              icon: 'mdp-ListAndLabel',
              items: vm.navMenuItems,
              onClick: handleMenuItem
            }, null, 8, ["button-label", "items"]))
          : _createCommentVNode("", true),
        _createVNode(_component_b_button, {
          class: "btn-with-icon mono",
          "data-cy": "last-export-btn",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (goToLastExport())),
          disabled: _unref(route).params.id === _unref(Guid).empty || !vm.hasPreviousExport
        }, {
          default: _withCtx(() => [
            _hoisted_6,
            _createElementVNode("span", null, _toDisplayString(_unref($t)("lastExport")), 1)
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      _: 1
    }),
    _createVNode(TranslationTableDialog, {
      "show-translation-table": vm.showTranslationDialog,
      "onUpdate:showTranslationTable": _cache[2] || (_cache[2] = ($event: any) => ((vm.showTranslationDialog) = $event))
    }, null, 8, ["show-translation-table"])
  ]))
}
}

})